import React, { useState, useEffect } from 'react';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    fallback?: React.ReactNode;
}

export const LazyImage: React.FC<LazyImageProps> = ({ src, alt, className, fallback, ...props }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!src) {
            setLoaded(false);
            return;
        }

        // Reset state for new src
        setLoaded(false);
        setError(false);

        const img = new window.Image();
        img.src = src;
        img.onload = () => setLoaded(true);
        img.onerror = () => setError(true);
    }, [src]);

    if (error) {
        return (
            <div className={`${className} bg-brand-900 flex items-center justify-center text-brand-600 border border-brand-800 rounded-lg`}>
                <span className="text-xs">Failed to load image</span>
            </div>
        );
    }

    return (
        <div className={`relative ${className}`}>
            {!loaded && (
                <div className="absolute inset-0 bg-brand-900 animate-pulse flex items-center justify-center rounded-lg border border-brand-800">
                    {fallback || <div className="w-8 h-8 border-2 border-gold-500/20 border-t-gold-500 rounded-full animate-spin"></div>}
                </div>
            )}
            {src && (
                <img
                    src={src}
                    alt={alt}
                    className={`${className} transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                    loading="lazy"
                    {...props}
                />
            )}
        </div>
    );
};
